import { UniButton } from '@/design-system/components/buttons';
import { UniCheckbox, UniInput } from '@/design-system/components/inputs';
import { UniTabHeader } from '@/design-system/components/tabs';
import { TextBody, TextTitle } from '@/design-system/components/typography';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { ScrollView, SxProp, View } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStackRoutes } from 'types';
import { HeaderBackgroundImageComponent } from '@/features/bill-payment/screens/components/HeaderBackgroundImageComponent';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import pinyin from 'pinyin';
import { getCJKCharactersLength } from '@/features/bill-payment/utils/utils';
import { UseInputRegularVariant } from '@/design-system/components/inputs/useInput.hook';
import { useTransferPeerValidate } from '@/features/bill-payment/hooks/useUnionPay.hook';
import binCodes from '@/features/bill-payment/i18n/binCode.json';
import { UnionType } from '@/features/bill-payment/enum';
import { useTransferPeerValidatePayout } from '@/features/account-management/screens/details/hooks/usePayouts.hook';
import { UnionPayAccount, ValidatePayoutBodyMethod } from '@utx/utx-access-client';

export interface AddNewAccountNativeScreenProps {
  onBack?: (flag?: boolean) => void;
  onAccountSubmit?: (flag: boolean, data: any) => void;
  transferPeerCountFlag?: boolean;
  wallet?: any;
  calcFee?: any;
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const AddNewAccountNativeScreen: React.FC<AddNewAccountNativeScreenProps> = (props) => {
  const {
    onAccountSubmit,
    transferPeerCountFlag = true,
    onBack,
    wallet,
    calcFee,
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('bill-payment-union-pay');
  const layout = useContext(LayoutContext);
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();

  const [accountData, setAccountData] = useState<any>({
    lastNameChinese: undefined,
    firstNameChinese: undefined,
    firstNameEnglish: undefined,
    lastNameEnglish: undefined,
    accountNumber: undefined,
    accountHolderAddress: undefined,
    isSave: unionType == UnionType.PAYMENT ? true : false,
    label: undefined,
    accountLabel: undefined
  });
  const [errorMsg, setErrorMsg] = useState<{
    lastNameChinese: string | undefined;
    firstNameChinese: string | undefined;
    firstNameEnglish: string | undefined;
    lastNameEnglish: string | undefined;
    accountNumber: string | undefined;
    accountHolderAddress: string | undefined;
    label: string | undefined;
  }>({
    lastNameChinese: undefined,
    firstNameChinese: undefined,
    firstNameEnglish: undefined,
    lastNameEnglish: undefined,
    accountNumber: undefined,
    accountHolderAddress: undefined,
    label: undefined
  });

  const { transferPeerSchemaByType, runTransferPeerValidate } = useTransferPeerValidate({ type: 'UNIONPAY' });
  const postTransferPeerValidate = (attributes: any, label?: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    runTransferPeerValidate(
      {
        transferPeerTypeId: transferPeerSchemaByType?.id,
        attributes,
        label
      },
      onSuccess,
      onError
    );
  };

  const { runTransferPeerValidatePayout } = useTransferPeerValidatePayout();
  const postTransferPeerValidatePayout = (
    method: ValidatePayoutBodyMethod,
    destination: UnionPayAccount,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    runTransferPeerValidatePayout(
      {
        method,
        destination
      },
      onSuccess,
      onError
    );
  };

  const validateBinCode = (value: string) => {
    const binCode = Object.keys(binCodes).find((bc) => value.search(bc) == 0);
    return binCode ? true : false;
  };

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };
    let accountNumberBinCode = true;
    if ((!keysLength && accountData.accountNumber) || (keys.includes('accountNumber') && data?.accountNumber)) {
      let accountNumber = !keysLength ? accountData.accountNumber : data?.accountNumber;
      if (accountNumber.length >= 6) {
        const binCode = validateBinCode(accountNumber);
        if (!binCode) {
          accountNumberBinCode = false;
        } else if (!(accountNumber.length >= 16 && accountNumber.length <= 19)) {
          accountNumberBinCode = false;
        }
      } else {
        accountNumberBinCode = false;
      }
    }

    if ((!keysLength && !accountData.lastNameChinese) || (keys.includes('lastNameChinese') && !data?.lastNameChinese)) {
      errorMsgFlag.lastNameChinese = t('unionPay::payment-instruction.field-required');
      validateFlag = false;
    } else if ((!keysLength && accountData.lastNameChinese) || (keys.includes('lastNameChinese') && data?.lastNameChinese)) {
      errorMsgFlag.lastNameChinese = undefined;
    }

    if ((!keysLength && !accountData.firstNameChinese) || (keys.includes('firstNameChinese') && !data?.firstNameChinese)) {
      errorMsgFlag.firstNameChinese = t('unionPay::payment-instruction.field-required');
      validateFlag = false;
    } else if ((!keysLength && accountData.firstNameChinese) || (keys.includes('firstNameChinese') && data?.firstNameChinese)) {
      errorMsgFlag.firstNameChinese = undefined;
    }

    if ((!keysLength && !accountData.firstNameEnglish) || (keys.includes('firstNameEnglish') && !data?.firstNameEnglish)) {
      errorMsgFlag.firstNameEnglish = t('unionPay::payment-instruction.field-required');
      validateFlag = false;
    } else if ((!keysLength && accountData.firstNameEnglish) || (keys.includes('firstNameEnglish') && data?.firstNameEnglish)) {
      errorMsgFlag.firstNameEnglish = undefined;
    }

    if ((!keysLength && !accountData.lastNameEnglish) || (keys.includes('lastNameEnglish') && !data?.lastNameEnglish)) {
      errorMsgFlag.lastNameEnglish = t('unionPay::payment-instruction.field-required');
      validateFlag = false;
    } else if ((!keysLength && accountData.lastNameEnglish) || (keys.includes('lastNameEnglish') && data?.lastNameEnglish)) {
      errorMsgFlag.lastNameEnglish = undefined;
    }

    if ((!keysLength && !accountData.accountNumber) || (keys.includes('accountNumber') && !data?.accountNumber)) {
      errorMsgFlag.accountNumber = t('unionPay::payment-instruction.field-required');
      validateFlag = false;
    } else if ((!keysLength && accountData.accountNumber) || (keys.includes('accountNumber') && data?.accountNumber)) {
      if (!accountNumberBinCode) {
        errorMsgFlag.accountNumber = t('unionPay::payment-instruction.account-number-incorrect');
        validateFlag = false;
      } else {
        errorMsgFlag.accountNumber = undefined;
      }
    }

    if ((!keysLength && !accountData.accountHolderAddress) || (keys.includes('accountHolderAddress') && !data?.accountHolderAddress)) {
      errorMsgFlag.accountHolderAddress = t('unionPay::payment-instruction.field-required');
      validateFlag = false;
    } else if ((!keysLength && accountData.accountHolderAddress) || (keys.includes('accountHolderAddress') && data?.accountHolderAddress)) {
      if (
        getCJKCharactersLength(!keys.includes('accountHolderAddress') ? accountData.accountHolderAddress : data?.accountHolderAddress) > 50
      ) {
        errorMsgFlag.accountHolderAddress = t('unionPay::glossary.exceed-max-character');
        validateFlag = false;
      } else {
        errorMsgFlag.accountHolderAddress = undefined;
      }
    }

    if (((!keysLength && !accountData.label) || (keys.includes('label') && !data?.label)) && accountData.isSave) {
      errorMsgFlag.label = t('unionPay::payment-instruction.field-required');
      validateFlag = false;
    } else if (((!keysLength && accountData.label) || (keys.includes('label') && data?.label)) && accountData.isSave) {
      errorMsgFlag.label = undefined;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const validateSubmit = async () => {
    const validateFlag = await validateForm();
    if (validateFlag) {
      let accountDataFlag = {
        ...accountData,
        accountLabel: `${accountData.lastNameEnglish || ''} ${accountData.firstNameEnglish || ''}`
      };

      if (accountData.isSave) {
        postTransferPeerValidate(
          {
            receiverAccountNumber: accountData.accountNumber,
            receiverFirstNameCN: accountData.firstNameChinese,
            receiverSurnameCN: accountData.lastNameChinese,
            receiverFirstName: accountData.firstNameEnglish,
            receiverSurname: accountData.lastNameEnglish,
            receiverAddress: accountData.accountHolderAddress,
            receiverIdNumber: '',
            receiverIdType: ''
          },
          accountData.label,
          (data: any) => {
            if (!data?.data?.success) {
              setErrorMsg({ ...errorMsg, label: t('unionPay::payment-instruction.label-already-exists') });
            } else {
              if (transferPeerCountFlag) {
                onAccountSubmit && onAccountSubmit(true, accountDataFlag);
                navigation.goBack();
              } else {
                navigationAny.navigate(navigationNavigate, {
                  screen: navigationScreen,
                  params: {
                    screen: UnionPayStackRoutes.Information,
                    onBack,
                    transferPeer: accountDataFlag,
                    wallet,
                    calcFee,
                    transferPeerCountFlag: false,
                    unionType,
                    refresh
                  }
                });
              }
            }
          },
          (error: any) => {
            console.log('error', JSON.stringify(error));
          }
        );
      } else {
        if (transferPeerCountFlag) {
          onAccountSubmit && onAccountSubmit(true, accountDataFlag);
          navigation.goBack();
        } else {
          navigationAny.navigate(navigationNavigate, {
            screen: navigationScreen,
            params: {
              screen: UnionPayStackRoutes.Information,
              onBack,
              transferPeer: accountDataFlag,
              wallet,
              calcFee,
              transferPeerCountFlag: false,
              unionType,
              refresh
            }
          });
        }
      }
    }
  };

  return (
    <View sx={{ ...sxStyles.container, ...(!transferPeerCountFlag ? { flex: 1 } : {}) }}>
      <View sx={{ ...sxStyles.header }}>
        <HeaderBackgroundImageComponent />
        <UniTabHeader
          title={t('unionPay::payment-instruction.title--add-new-account')}
          showBackButton={navigation.canGoBack()}
          onBack={() => navigation.goBack()}
          labelColor="$white"
          sx={{ ...sxStyles.tab, px: '$4', py: '$5' }}
        />
      </View>
      <ScrollView sx={sxStyles.scrollViewWrapper} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          <View
            sx={
              layout.dimensions.height >= 740 ? { height: layout.dimensions.height - 136 - (unionType == UnionType.ACCOUNT ? 40 : 0) } : {}
            }>
            <View sx={sxStyles.topHeader}>
              <View sx={sxStyles.textMb}>
                <TextTitle bold>{t('unionPay::payment-instruction.title--add-new-unionpay-account')}</TextTitle>
                <TextBody>{t('unionPay::payment-instruction.text--provide-following-information')}</TextBody>
              </View>
            </View>
            <View sx={{ gap: 20 }}>
              <View sx={{ gap: '$5', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <UniInput
                  labelText={t('unionPay::payment-instruction.text--last-name-chinese')}
                  errorText={errorMsg.lastNameChinese}
                  onValueChange={(value) => {
                    const pinyinData = pinyin(value || '', {
                      style: 0,
                      mode: 'SURNAME',
                      segment: 'segmentit'
                    });
                    let lastNameEnglish = pinyinData.join(' ').toUpperCase();
                    let label = accountData.isSave
                      ? `${lastNameEnglish || ''} ${accountData.firstNameEnglish || ''} / ${value || ''}${
                          accountData.firstNameChinese || ''
                        }`
                      : undefined;
                    setAccountData({
                      ...accountData,
                      lastNameChinese: value,
                      lastNameEnglish: lastNameEnglish,
                      label: label
                    });
                    validateForm({ lastNameChinese: value, lastNameEnglish: lastNameEnglish, label: label });
                  }}
                />
                <UniInput
                  labelText={t('unionPay::payment-instruction.text--first-name-chinese')}
                  errorText={errorMsg.firstNameChinese}
                  onValueChange={(value) => {
                    const pinyinData = pinyin(value || '', {
                      style: 0,
                      mode: 'NORMAL',
                      segment: 'segmentit'
                    });
                    let firstNameEnglish = pinyinData.join(' ').toUpperCase();
                    let label = accountData.isSave
                      ? `${accountData.lastNameEnglish || ''} ${firstNameEnglish || ''} / ${accountData.lastNameChinese || ''}${
                          value || ''
                        }`
                      : undefined;
                    setAccountData({
                      ...accountData,
                      firstNameChinese: value,
                      firstNameEnglish: firstNameEnglish,
                      label: label
                    });
                    validateForm({ firstNameChinese: value, firstNameEnglish: firstNameEnglish, label: label });
                  }}
                />
              </View>
              <UniInput
                value={accountData.firstNameEnglish}
                labelText={t('unionPay::payment-instruction.text--first-name-english')}
                errorText={errorMsg.firstNameEnglish}
                onValueChange={(value) => {
                  let label = accountData.isSave
                    ? `${accountData.lastNameEnglish || ''} ${value || ''} / ${accountData.lastNameChinese || ''}${
                        accountData.firstNameChinese || ''
                      }`
                    : undefined;
                  setAccountData({
                    ...accountData,
                    firstNameEnglish: value,
                    label: label
                  });
                  validateForm({ firstNameEnglish: value, label: label });
                }}
              />
              <UniInput
                value={accountData.lastNameEnglish}
                labelText={t('unionPay::payment-instruction.text--last-name-english')}
                errorText={errorMsg.lastNameEnglish}
                onValueChange={(value) => {
                  let label = accountData.isSave
                    ? `${value || ''} ${accountData.firstNameEnglish || ''} / ${accountData.lastNameChinese || ''}${
                        accountData.firstNameChinese || ''
                      }`
                    : undefined;
                  setAccountData({
                    ...accountData,
                    lastNameEnglish: value,
                    label: label
                  });
                  validateForm({ lastNameEnglish: value, label: label });
                }}
              />
              <UniInput
                value={accountData.accountNumber}
                labelText={t('unionPay::payment-instruction.text--account-number')}
                errorText={errorMsg.accountNumber}
                onValueChange={(value) => {
                  let accountNumber = value?.replace(/[^\d]/g, '');
                  setAccountData({ ...accountData, accountNumber: accountNumber });
                  validateForm({ accountNumber: accountNumber });
                }}
              />
              <View>
                <UniInput
                  labelText={t('unionPay::payment-instruction.text--account-holder-address')}
                  errorText={errorMsg.accountHolderAddress}
                  multiline
                  textAlignVertical="top"
                  sx={{ height: 97, alignItems: 'flex-start' }}
                  inputSx={{ height: 97, lineHeight: 20, pt: 14, pb: 23 }}
                  wrapperSx={{ height: 119 }}
                  regular={UseInputRegularVariant.cJKCharacters}
                  unicodeCharsLength={50}
                  onValueChange={(value) => {
                    setAccountData({ ...accountData, accountHolderAddress: value });
                    validateForm({ accountHolderAddress: value });
                  }}
                />
              </View>
              {unionType == UnionType.PAYMENT && (
                <>
                  <UniCheckbox
                    sx={{ alignItems: 'center' }}
                    label={t('unionPay::payment-instruction.text--save-account-for-future-use')}
                    value={accountData.isSave}
                    onValueChange={(value) => {
                      setAccountData({
                        ...accountData,
                        isSave: value,
                        label: value
                          ? `${accountData.firstNameEnglish || ''} ${accountData.lastNameEnglish || ''} / ${
                              accountData.lastNameChinese || ''
                            }${accountData.firstNameChinese || ''}`
                          : undefined
                      });
                    }}
                  />
                  <UniInput
                    disabled={accountData.isSave ? false : true}
                    value={accountData.label}
                    labelText={t('unionPay::payment-instruction.text--label')}
                    errorText={errorMsg.label}
                    onValueChange={(value) => {
                      setAccountData({ ...accountData, label: value });
                    }}
                  />
                </>
              )}
            </View>
          </View>
          <View sx={sxStyles.btnView}>
            <UniButton
              sx={{ height: 44 }}
              labelSx={{ color: '$balck' }}
              label={t('unionPay::payment-instruction.button--add-account')}
              onPress={validateSubmit}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%'
  },
  header: {
    flexDirection: 'column'
    // padding: '$4'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  body: {
    flex: 1,
    zIndex: 10
  },
  scrollViewWrapper: {
    pt: '$5',
    pb: '$7',
    px: '$4'
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  },
  topHeader: {
    justifyContent: 'center',
    paddingBottom: '$5',
    gap: '$2'
  },
  textMb: {
    gap: 8
  },
  btnView: {
    width: '100%',
    left: 0,
    bottom: 0,
    pt: '$10'
  },
  dropdown: {
    gap: '$5'
  }
};
